import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { getToolDisplayName } from '../lib/utils';

interface ToolCall {
  id: string;
  tool: string;
  params: Record<string, any>;
  result?: any;
  error?: string;
}

interface ExpandableToolPillProps {
  toolCall: ToolCall;
  /** Compact mode for drawer vs full-page */
  compact?: boolean;
}

/**
 * Extracts a short summary line from tool params/result.
 * Returns null if nothing meaningful to show.
 */
function getToolSummary(tool: string, params: Record<string, any>, result?: any): string[] | null {
  const lines: string[] = [];

  switch (tool) {
    case 'start_workflow':
      if (result?.program_name) lines.push(`Program: ${result.program_name}`);
      else if (params?.name) lines.push(`Program: ${params.name}`);
      if (result?.workflow_name) lines.push(`Workflow: ${result.workflow_name}`);
      if (result?.success === false && result?.error) lines.push(String(result.error));
      break;

    case 'search_market': {
      if (params?.query) lines.push(`Query: "${params.query}"`);
      if (result?.total != null) lines.push(`${result.total} result${result.total !== 1 ? 's' : ''} found`);
      if (result?.products?.length) {
        const top = result.products.slice(0, 3);
        top.forEach((p: any) => {
          const score = p.overall_score != null ? ` (${Math.round(p.overall_score)})` : '';
          lines.push(`${p.vendor_name} — ${p.product_name}${score}`);
        });
        if (result.products.length > 3) {
          lines.push(`+${result.products.length - 3} more`);
        }
      }
      break;
    }

    case 'search_specs': {
      if (params?.query) lines.push(`Query: "${params.query}"`);
      if (result?.count != null) lines.push(`${result.count} spec${result.count !== 1 ? 's' : ''} found`);
      if (result?.results?.length) {
        const top = result.results.slice(0, 3);
        top.forEach((s: any) => {
          const unit = s.unit ? ` (${s.unit})` : '';
          lines.push(`${s.name}${unit}`);
        });
        if (result.results.length > 3) {
          lines.push(`+${result.results.length - 3} more`);
        }
      }
      break;
    }

    case 'add_scorecard_item':
      if (result?.title) lines.push(`Item: ${result.title}`);
      if (result?.created_spec_id) lines.push('New spec created');
      if (params?.spec_name && !result?.title) lines.push(`Spec: ${params.spec_name}`);
      break;

    case 'update_scorecard_item':
      if (params?.title) lines.push(`Item: ${params.title}`);
      break;

    case 'remove_scorecard_item':
      if (params?.title) lines.push(`Removed: ${params.title}`);
      break;

    case 'get_vendor_detail':
      if (result?.vendor_name) lines.push(`Vendor: ${result.vendor_name}`);
      if (result?.products?.length) lines.push(`${result.products.length} product${result.products.length !== 1 ? 's' : ''}`);
      break;

    case 'list_program_vendors':
      if (result?.total_vendors != null) lines.push(`${result.total_vendors} vendor${result.total_vendors !== 1 ? 's' : ''}`);
      if (result?.vendors?.length) {
        const top = result.vendors.slice(0, 3);
        top.forEach((v: any) => lines.push(v.vendor_name));
        if (result.vendors.length > 3) {
          lines.push(`+${result.vendors.length - 3} more`);
        }
      }
      break;

    case 'get_program':
    case 'get_program_summary':
      if (result?.name) lines.push(`Program: ${result.name}`);
      if (result?.status) lines.push(`Status: ${result.status}`);
      break;

    case 'update_agency_need':
      if (params?.agency_need) {
        const need = params.agency_need;
        lines.push(need.length > 80 ? need.slice(0, 80) + '…' : need);
      }
      break;

    case 'list_program_documents':
      if (result?.length != null) lines.push(`${result.length} document${result.length !== 1 ? 's' : ''}`);
      if (Array.isArray(result)) {
        result.slice(0, 3).forEach((d: any) => {
          if (d.title || d.name) lines.push(d.title || d.name);
        });
      }
      break;

    case 'get_document':
    case 'edit_document':
      if (result?.title) lines.push(`Document: ${result.title}`);
      else if (params?.title) lines.push(`Document: ${params.title}`);
      break;

    case 'list_technologies':
      if (Array.isArray(result)) {
        lines.push(`${result.length} technolog${result.length !== 1 ? 'ies' : 'y'}`);
      }
      break;

    case 'research_technology_market':
      if (params?.description) {
        const d = params.description;
        lines.push(d.length > 80 ? d.slice(0, 80) + '…' : d);
      } else if (params?.technology_id) {
        lines.push(`Technology #${params.technology_id}`);
      }
      break;

    case 'get_scorecard':
    case 'list_program_scorecards':
      if (result?.name) lines.push(`Scorecard: ${result.name}`);
      if (Array.isArray(result)) lines.push(`${result.length} scorecard${result.length !== 1 ? 's' : ''}`);
      break;

    default:
      // Generic: show error if present
      break;
  }

  if (result?.error) {
    lines.push(`Error: ${result.error}`);
  }

  return lines.length > 0 ? lines : null;
}

const ExpandableToolPill: React.FC<ExpandableToolPillProps> = ({ toolCall, compact = false }) => {
  const [expanded, setExpanded] = useState(false);
  const summary = getToolSummary(toolCall.tool, toolCall.params, toolCall.result);
  const hasDetails = summary !== null;
  const isLoading = !toolCall.result && !toolCall.error;

  return (
    <div className={compact ? 'my-1' : 'my-1.5'}>
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`inline-flex items-center gap-1 ${compact ? 'px-2 py-0.5' : 'px-3 py-1'} bg-slate-200 dark:bg-slate-600 rounded-full text-xs text-slate-600 dark:text-slate-300 transition-colors ${
          hasDetails ? 'hover:bg-slate-300 dark:hover:bg-slate-500 cursor-pointer' : 'cursor-default'
        }`}
      >
        <span className="opacity-70">&#9881;</span>
        <span>{getToolDisplayName(toolCall.tool)}</span>
        {isLoading && (
          <span className="inline-block w-3 h-3 border border-slate-400 dark:border-slate-300 border-t-transparent rounded-full animate-spin" />
        )}
        {hasDetails && !isLoading && (
          <ChevronDown
            className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      {expanded && summary && (
        <div className={`mt-1 ${compact ? 'ml-2 pl-2' : 'ml-3 pl-3'} border-l-2 border-slate-300 dark:border-slate-500 text-xs text-slate-500 dark:text-slate-400 space-y-0.5`}>
          {summary.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpandableToolPill;
